import { Paragraph } from "@smartsuite/react-ui/lib";
import React from "react";
import { isLookupFieldStale } from "../../helpers/structure";
import type { FieldVisibilityCallback } from "../../hooks/useFormFieldVisibility";
import { FieldControlComponent } from "../../registry/FieldControlComponent";
import { Fields } from "../../types/fields";
import type { FormConfigApplicationStructureItem, FormConfigItem } from "../../types/form";
import { $t } from "../../utils/intl";
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary";
import { formFieldItemText } from "./FormFieldItem.text";

export interface FormFieldItemProps {
  appField?: FormConfigApplicationStructureItem;
  item: FormConfigItem;
  checkFieldVisibility: FieldVisibilityCallback;
}

const FormFieldItemRaw: React.FC<FormFieldItemProps> = ({
  appField,
  item,
  checkFieldVisibility,
}) => {
  if (!appField) return null;
  if (isLookupFieldStale(item, appField)) return null;

  const isItemConditionSatisfied = checkFieldVisibility(item.slug);

  const shouldShowField =
    isItemConditionSatisfied ||
    [Fields.userfield, Fields.linkedrecordfield, Fields.teamfield].includes(appField.field_type);

  if (!shouldShowField) return null;

  const label = item.params.label ?? appField.label;

  return (
    <div
      key={item.slug}
      className="form-field-item"
      data-testid="form-field-item"
      data-field-name={item.slug}
    >
      {/* One control throwing must not cost the whole form. Without a boundary here the throw
          reaches GlobalErrorBoundary and every field is replaced by an error screen — that is how
          a bad checkpoint value (FORMS-WEB-XG, X1, X5) and an injected script tripping over an
          Icon's <use> element (FORMS-WEB-X4) each took a form down. The fallback is visible on
          purpose: a silently missing field invites an incomplete submission. */}
      <ErrorBoundary
        fallback={
          <div className="form-field-item__error" data-testid="form-field-item-error">
            <Paragraph size="xs" color="error">
              {$t(formFieldItemText.fieldFailed, { label })}
            </Paragraph>
          </div>
        }
      >
        <FieldControlComponent
          formItem={item}
          field={appField}
          isItemConditionSatisfied={isItemConditionSatisfied}
        />
      </ErrorBoundary>
    </div>
  );
};

export const FormFieldItem = React.memo(FormFieldItemRaw);
